import React from 'react';
import PremiumLayout from './PremiumLayout';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { getFeaturedArticles, getArticles, Article } from '../../services/articles';
import { getSponsors, Sponsor as ApiSponsor } from '../../services/sponsors';
import { api as axiosApi } from '../../services/api';
import { assetUrl } from '../../utils/url';
import { getPlayers, Player as ApiPlayer } from '../../services/players';
import { getClothing, ClothingItem } from '../../services/clothing';
import { Box, Heading, Text, SimpleGrid } from '@chakra-ui/react';
import { facrApi } from '../../services/facr/facrApi';
import { FACR_CLUB_ID, FACR_CLUB_TYPE } from '../../config/facr';
import NewsList, { NewsListItem } from '../../components/pack/NewsList';
import StandingsCard, { StandingRow } from '../../components/pack/StandingsCard';
import NextMatch, { NextMatchData } from '../../components/pack/NextMatch';
import VideosSection from '../../components/home/VideosSection';
import MerchSection from '../../components/home/MerchSection';
import SpartaHorizontalSlider from '../../components/elements/SpartaHorizontalSlider';

const LegacyPremiumHomePage: React.FC = () => {
  const { data: settings } = usePublicSettings();
  const clubName = settings?.club_name || 'Fotbal Club';
  const igUrl = React.useMemo(() => {
    const u = (settings?.instagram_url || '').trim();
    if (!u) return '';
    const rx = /^https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|tv)\/[^\/?#]+/i;
    return rx.test(u) ? u : '';
  }, [settings?.instagram_url]);

  // Build zoom slider images from featured/news
  const [heroImages, setHeroImages] = React.useState<string[]>([]);
  const [sponsors, setSponsors] = React.useState<ApiSponsor[]>([]);
  const [merch, setMerch] = React.useState<ClothingItem[]>([]);

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [featured, latest] = await Promise.all([
          getFeaturedArticles({ page_size: 3 }).catch(() => ({ data: [] as Article[] })),
          getArticles({ page: 1, page_size: 8, published: true }).catch(() => ({ data: [] as Article[] })),
        ]);
        const imgs = ([] as (string | undefined)[])
          .concat((featured?.data || []).map(a => a.image_url))
          .concat((latest?.data || []).map(a => a.image_url))
          .slice(0, 5)
          .map(u => assetUrl(u))
          .filter((u): u is string => typeof u === 'string');
        if (active) setHeroImages(imgs as string[]);
      } catch {}
      try {
        const s = await getSponsors();
        if (active) setSponsors(s || []);
      } catch {}
      try {
        const m = await getClothing();
        if (active) setMerch(m || []);
      } catch {}
    })();
    return () => { active = false; };
  }, []);

  // Trigger social embed parsing after mount and when social URLs change (SPA)
  React.useEffect(() => {
    const w: any = window as any;
    try { if (w.FB && w.FB.XFBML && typeof w.FB.XFBML.parse === 'function') w.FB.XFBML.parse(); } catch {}
    try { if (w.instgrm && w.instgrm.Embeds && typeof w.instgrm.Embeds.process === 'function') w.instgrm.Embeds.process(); } catch {}
  }, [settings?.facebook_url, settings?.instagram_url]);

  // Initialize zoom slider and theme widgets if present
  React.useEffect(() => {
    const timer = setTimeout(() => {
      try {
        const w: any = window as any;
        if (w && w.jQuery && typeof w.jQuery === 'function') {
          const $ = w.jQuery;
          if ($ && typeof ($ as any).fn?.zoomslider === 'function') {
            $('.lte-slider-zoom').each((_i: any, el: any) => {
              try { ($ as any)(el).zoomslider(); } catch {}
            });
          }
        }
        if (typeof (w as any).initSwiperWrappers === 'function') {
          (w as any).initSwiperWrappers();
        }
      } catch {}
    }, 50);
    return () => clearTimeout(timer);
  }, [heroImages]);

  // Re-init swiper when merch arrives
  React.useEffect(() => {
    if (!merch.length) return;
    const w: any = window as any;
    try { if (typeof w.initSwiperWrappers === 'function') w.initSwiperWrappers(); } catch {}
  }, [merch]);

  // Populate dynamic sections similar to pro/js/* in TS
  React.useEffect(() => {
    let cancelled = false;

    function h(tag: string, attrs: Record<string, any> = {}, children: (HTMLElement | string)[] = []) {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === 'class') el.className = v;
        else if (k === 'html') el.innerHTML = v;
        else el.setAttribute(k, String(v));
      });
      children.forEach(c => {
        if (typeof c === 'string') el.appendChild(document.createTextNode(c));
        else el.appendChild(c);
      });
      return el;
    }

    // Helpers
    const fetchJSON = async (url: string) => {
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) return null;
        return await res.json();
      } catch { return null; }
    };

    // Blog latest (primary 4)
    async function renderLatestBlog() {
      const mount = document.getElementById('latest-blog-items');
      if (!mount) return;
      mount.innerHTML = '<div style="width:100%;text-align:center;padding:12px;color:#888;">Načítání…</div>';
      try {
        const resp = await getArticles({ page: 1, page_size: 12, published: true });
        if (cancelled) return;
        const items = (resp?.data || []).slice(0, 4);
        const frag = document.createDocumentFragment();
        items.forEach((a) => {
          const col = h('div', { class: 'item div-thumbnail col-xl-6 col-lg-6 col-md-6 col-sm-6 col-ms-6 col-xs-12' });
          const art = h('article', { class: 'post type-post has-post-thumbnail hentry' });
          const url = a.slug ? `/news/${a.slug}` : `/articles/${a.id}`;
          const aPhoto = h('a', { href: url, class: 'lte-photo' });
          const img = h('img', { src: assetUrl(a.image_url) || '/images/news/placeholder.jpg', width: '500', height: '300', decoding: 'async', class: 'attachment-atleticos-blog size-atleticos-blog wp-post-image', alt: '' });
          aPhoto.appendChild(img);
          aPhoto.appendChild(h('span', { class: 'lte-photo-overlay' }));
          const descr = h('div', { class: 'lte-description' });
          const aHeader = h('a', { href: url, class: 'lte-header' });
          aHeader.appendChild(h('h3', { html: a.title }));
          descr.appendChild(aHeader);
          art.appendChild(aPhoto); art.appendChild(descr); col.appendChild(art);
          frag.appendChild(col);
        });
        mount.innerHTML = '';
        mount.appendChild(frag);
        // Re-init Masonry for the home grid
        try {
          const w: any = window as any;
          const $: any = (w && (w.jQuery || w.$)) || null;
          if ($ && typeof $.fn.imagesLoaded === 'function') {
            $(mount).imagesLoaded(() => { try { if (typeof w.initMasonry === 'function') w.initMasonry(); } catch {} });
          } else {
            try { if (typeof w.initMasonry === 'function') w.initMasonry(); } catch {}
          }
        } catch {}
      } catch {
        mount.innerHTML = '<div style="width:100%;text-align:center;padding:12px;color:#c00;">Nepodařilo se načíst novinky.</div>';
      }
    }

    // Videos latest using backend YouTube cache
    async function renderLatestVideos() {
      const featureMount = document.getElementById('latest-video-feature');
      const gridMount = document.getElementById('latest-videos-grid');
      if (!featureMount && !gridMount) return;
      if (featureMount) featureMount.innerHTML = '<div style="width:100%;text-align:center;padding:12px;color:#888;">Načítání…</div>';
      try {
        const res = await axiosApi.get('/youtube/videos');
        if (cancelled) return;
        const items = (res?.data?.videos || []) as Array<{ video_id: string; title: string; thumbnail_url: string; published_text?: string; }>
        if (featureMount) featureMount.innerHTML = '';
        if (!items.length) return;
        const first = items[0];
        if (featureMount && first) {
          const container = h('div', { class: 'items col-xl-12 col-lg-12 col-md-12 col-sm-12 col-ms-12 col-xs-12' });
          const article = h('article', { class: 'post format-video has-post-thumbnail hentry' });
          const wrap = h('div', { class: 'lte-wrapper' });
          const aEl = h('a', { href: `https://www.youtube.com/watch?v=${first.video_id}`, target: '_blank', class: 'lte-photo lte-video-popup swipebox' });
          aEl.appendChild(h('img', { loading: 'lazy', decoding: 'async', width: '1600', height: '969', src: first.thumbnail_url, class: 'attachment-full size-full wp-post-image', alt: '' }));
          const iconWrap = h('span', { class: 'lte-icon-video' });
          iconWrap.appendChild(h('span', { html: '' }));
          aEl.appendChild(iconWrap);
          wrap.appendChild(aEl);
          const descr = h('div', { class: 'lte-description' });
          const headerA = h('a', { href: `https://www.youtube.com/watch?v=${first.video_id}`, class: 'lte-header', target: '_blank' });
          headerA.appendChild(h('h3', { html: first.title || '' }));
          descr.appendChild(headerA);
          descr.appendChild(h('div', { class: 'lte-excerpt' }));
          article.appendChild(wrap); article.appendChild(descr); container.appendChild(article);
          featureMount.appendChild(container);
        }
        if (gridMount) {
          const frag = document.createDocumentFragment();
          items.slice(1, 5).forEach(v => {
            const col = h('div', { class: 'items col-xl-6 col-lg-6 col-md-6 col-sm-6 col-ms-12 col-xs-12' });
            const article = h('article', { class: 'post format-video has-post-thumbnail hentry' });
            const wrap = h('div', { class: 'lte-wrapper' });
            const aEl = h('a', { href: `https://www.youtube.com/watch?v=${v.video_id}`, target: '_blank', class: 'lte-photo lte-video-popup swipebox' });
            aEl.appendChild(h('img', { loading: 'lazy', decoding: 'async', width: '1600', height: '969', src: v.thumbnail_url, class: 'attachment-full size-full wp-post-image', alt: '' }));
            const iconWrap = h('span', { class: 'lte-icon-video' });
            iconWrap.appendChild(h('span', { html: '' }));
            aEl.appendChild(iconWrap);
            wrap.appendChild(aEl);
            const descr = h('div', { class: 'lte-description' });
            const headerA = h('a', { href: `https://www.youtube.com/watch?v=${v.video_id}`, class: 'lte-header', target: '_blank' });
            headerA.appendChild(h('h3', { html: v.title || '' }));
            descr.appendChild(headerA);
            descr.appendChild(h('div', { class: 'lte-excerpt' }));
            article.appendChild(wrap); article.appendChild(descr); col.appendChild(article);
            frag.appendChild(col);
          });
          gridMount.appendChild(frag);
        }
      } catch {}
    }

    // FACR: upcoming + table from prefetch cache
    async function renderFACR() {
      const root = document.getElementById('facr-upcoming');
      const tbody = document.getElementById('facr-table-body');
      const tabs = document.getElementById('facr-comp-tabs');
      if (!root && !tbody) return;
      const clubInfo = await fetchJSON('/cache/prefetch/facr_club_info.json');
      const tables = await fetchJSON('/cache/prefetch/facr_tables.json');
      if (!clubInfo && !tables) return;

      // Upcoming
      try {
        const comps: any[] = Array.isArray(clubInfo?.competitions) ? clubInfo.competitions : [];
        const parseCZ = (s: string) => {
          try { const [d, t] = String(s||'').split(' '); const [day, month, year] = d.split('.').map(Number); const [hh, mm] = (t||'').split(':').map(Number); return new Date(year, (month||1)-1, day||1, hh||0, mm||0); } catch { return null; }
        };
        const candidates: Array<{ comp: any; match: any; dt: Date }>[] = comps.map((c: any) => (c.matches||[]).map((m:any)=>({ comp:c, match:m, dt: parseCZ(m.date_time) as Date})).filter((x: { dt: Date }) => x.dt instanceof Date));
        const now = Date.now();
        const threeD = 3*24*60*60*1000;
        const flat = candidates.map(list=> list.sort((a,b)=>a.dt.getTime()-b.dt.getTime()));
        const picks: Array<{ comp:any; match:any; dt:Date }> = [];
        flat.forEach(list => {
          if (!list.length) return;
          let pick = list.filter((x: { dt: Date }) => x.dt.getTime() <= now && now - x.dt.getTime() <= threeD).slice(-1)[0];
          if (!pick) pick = list.find((x: { dt: Date }) => x.dt.getTime() >= now) || list[list.length-1];
          if (pick) picks.push(pick);
        });
        picks.sort((a,b)=> a.dt.getTime()-b.dt.getTime());
        const sel = picks[0];
        if (root && sel) {
          const m = sel.match, c = sel.comp;
          const homeLogo = m.home_logo_url || '/dist/img/logo-club-empty.svg';
          const awayLogo = m.away_logo_url || '/dist/img/logo-club-empty.svg';
          const dateOnly = (()=>{ const d=sel.dt; return `${d.getDate()}. ${d.getMonth()+1}. ${d.getFullYear()}`; })();
          const timeToken = (String(m.date_time||'').split(' ')[1]||'').slice(0,5);
          const score = String(m.score||'');
          const s1 = score.includes(':') ? score.split(':')[0] : '';
          const s2 = score.includes(':') ? score.split(':')[1] : '';
          const midText = (sel.dt.getTime()>now) ? 'Začátek' : (score||'-');
          root.innerHTML = `
            <div class="lte-football-upcoming">
              <div class="facr-comp-title lte-football-date" style="text-align:center; margin-bottom:6px;">${c.name || c.code || 'Soutěž'}</div>
              <div class="lte-teams">
                <span class="lte-team-name lte-team-1 lte-header" title="${m.home}">
                  <span class="lte-team-logo"><img decoding="async" src="${homeLogo}"></span>${m.home}
                  ${s1 ? `<span class="lte-team-count-mob">${s1}</span>`: ''}
                </span>
                <span class="lte-team-count">
                  <span id="facr-mid" style="font-size:32px; font-weight:700; display:inline-block; min-width:120px; text-align:center;">${midText}</span>
                  ${s1 && s2 ? `<span class="facr-mob-center-score">${s1}<span>:</span>${s2}</span>` : ''}
                </span>
                <span class="lte-team-name lte-team-2 lte-header" title="${m.away}">
                  ${s2 ? `<span class=\"lte-team-count-mob\">${s2}</span>`: ''}${m.away}<span class="lte-team-logo"><img decoding="async" src="${awayLogo}"></span>
                </span>
              </div>
              <span class="lte-football-date" style="text-align:center;">${dateOnly}${m.venue?`, ${m.venue}`:''}</span>
              ${timeToken ? `<span class="lte-football-time" style="display:block; text-align:center;">${timeToken}</span>`:''}
            </div>`;
        }
      } catch {}

      // Table
      try {
        const compsTbl: any[] = Array.isArray(tables?.competitions) ? tables.competitions : [];
        if (tabs) {
          tabs.innerHTML = compsTbl.map((c: any, i: number) => `<button class="facr-tab ${i===0?'active':''}" data-idx="${i}">${c.name || c.code || 'Soutěž'}</button>`).join('');
          tabs.querySelectorAll('button').forEach((btn) => {
            btn.addEventListener('click', () => {
              tabs.querySelectorAll('button').forEach(b=> b.classList.remove('active'));
              btn.classList.add('active');
              const idx = Number((btn as HTMLButtonElement).dataset.idx||'0');
              const comp = compsTbl[Math.max(0, Math.min(idx, compsTbl.length-1))];
              const rows = comp?.table?.overall || [];
              if (tbody) {
                tbody.innerHTML = rows.map((r:any)=> `
                  <tr>
                    <td class="lte-row"><span>${r.rank||''}</span></td>
                    <td class="lte-club-logo"><img decoding="async" src="${r.team_logo_url || '/dist/img/logo-club-empty.svg'}"></td>
                    <td class="lte-name">${r.team||''}</td>
                    <td class="lte-rate">${r.played||''}</td>
                    <td class="lte-rate">${r.wins||''}</td>
                    <td class="lte-rate">${r.draws||''}</td>
                    <td class="lte-rate">${r.losses||''}</td>
                    <td class="lte-rate">${r.score||''}</td>
                    <td class="lte-summary">${r.points||''}</td>
                  </tr>`).join('');
              }
            });
          });
        }
        // initialize first
        const firstBtn = tabs?.querySelector('button') as HTMLButtonElement | null;
        if (firstBtn) firstBtn.click();
      } catch {}
    }

    // Team slider using Players API
    async function renderTeamSlider() {
      const wrapperEl = document.getElementById('team-swiper-wrapper-1') as HTMLElement | null;
      if (!wrapperEl) return;
      try {
        const list = await getPlayers();
        // Load teams to infer gender buckets from team names
        let rawTeams: any[] = [];
        try {
          const tRes = await axiosApi.get('/teams');
          rawTeams = Array.isArray(tRes.data) ? tRes.data : (Array.isArray((tRes.data as any)?.data) ? (tRes.data as any).data : []);
        } catch {}
        const genderByTeamId: Record<number, 'men' | 'women'> = {};
        rawTeams.forEach((t: any) => {
          const id = (t && (t.id ?? t.ID)) as number | undefined;
          const nm = String(t?.name ?? t?.Name ?? '').toLowerCase();
          if (!id) return;
          // Normalize accents for Czech keywords
          const norm = nm.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const isWomen = /(zeny|zena|zene|zen|women|girl)/i.test(norm);
          genderByTeamId[id] = isWomen ? 'women' : 'men';
        });

        let current: 'men' | 'women' = 'men';
        const switcher = document.getElementById('gender-switcher');

        function buildSlidesForGender(g: 'men'|'women') {
          const filtered = (list || []).filter((p: any) => {
            const pg = String(p?.gender ?? p?.Gender ?? '').toLowerCase();
            if (pg === 'men' || pg === 'women') return pg === g;
            const tid = p?.team_id ?? p?.TeamID;
            const gval = (typeof tid === 'number' && genderByTeamId[tid]) ? genderByTeamId[tid] : 'men';
            return gval === g;
          });
          const finalList: ApiPlayer[] = (filtered.length ? filtered : (list || [])).slice(0, 12);
          const html = finalList.map((p: ApiPlayer) => {
            const img = assetUrl(p.image_url) || '/dist/img/logo-club-empty.svg';
            const num = (p as any).jersey_number || '';
            const name = [p.first_name, p.last_name].filter(Boolean).join(' ');
            const role = (p as any).position || '';
            return `
              <div class="lte-item swiper-slide">
                <div class="lte-team-item">
                  <a class="lte-image" style="background-image: url()">
                    <img loading="lazy" decoding="async" width="800" height="1200" src="${img}" class="attachment-full size-full" />
                  </a>
                  <div class="lte-descr">
                    <div class="lte-num">${num||''}</div>
                    <a href="${img}" target="_blank"><h4 class="lte-header">${name}</h4></a>
                    <p class="lte-subheader"><span>${role}</span></p>
                  </div>
                </div>
              </div>`;
          }).join('');
          const wEl = document.getElementById('team-swiper-wrapper-1') as HTMLElement | null;
          if (!wEl) return;
          wEl.innerHTML = html;
          const w: any = window as any;
          if (typeof (w as any).initSwiperWrappers === 'function') {
            try { (w as any).initSwiperWrappers(); } catch {}
          }
        }

        // Bind UI switcher to update active button and slides
        if (switcher && !(switcher as any).__boundTS) {
          switcher.addEventListener('click', (ev: any) => {
            const target = (ev.target as HTMLElement);
            const btn = target && (target.closest ? target.closest('button[data-gender]') : null) as HTMLButtonElement | null;
            if (!btn) return;
            const g = (btn.dataset.gender === 'women') ? 'women' : 'men';
            current = g;
            Array.from(switcher.querySelectorAll('button[data-gender]')).forEach((b) => {
              const bb = b as HTMLButtonElement;
              bb.classList.toggle('active', (bb.dataset.gender === g));
            });
            buildSlidesForGender(current);
          });
          (switcher as any).__boundTS = true;
        }

        // Initial render
        buildSlidesForGender(current);
      } catch {}
    }

    renderLatestBlog();
    renderLatestVideos();
    renderFACR();
    renderTeamSlider();

    return () => { cancelled = true; };
  }, []);

  // Sponsors grid render (simple)
  const sponsorsGrid = (
    <div id="sponzori" className="elementor-element elementor-element-031d23b e-flex e-con-boxed e-con e-parent" data-core-v316-plus="true">
      <div className="e-con-inner">
        <div className="elementor-element elementor-widget elementor-widget-lte-partners">
          <div className="elementor-widget-container">
            <div className="container-fluid lte-partners-sc has-lte-divider  lte-hover-effect-opacity">
              <div className="row centered">
                {(sponsors || []).slice(0, 24).map((s) => (
                  <div key={s.id} className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-ms-6 col-xs-12 partners-wrap  center-flex">
                    <div className="partners-item item center-flex">
                      <a href={s.website_url || '#'} target="_blank" rel="noreferrer">
                        {/* eslint-disable-next-line jsx-a11y/alt-text */}
                        <img src={assetUrl(s.logo_url) || '/images/sponsors/placeholder.png'} className="image" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const zsAttr = heroImages.length ? JSON.stringify(heroImages) : JSON.stringify(['/dist/img/logo-club-empty.svg']);

  return (
    <PremiumLayout>
      <div className="lte-text-page margin-disabled">
        {/* Zoom slider */}
        <section className="elementor-section elementor-top-section elementor-section-full_width">
          <div className="elementor-container elementor-column-gap-no">
            <div className="elementor-column elementor-col-100 elementor-top-column">
              <div className="elementor-widget-wrap elementor-element-populated">
                <div className="elementor-widget elementor-widget-lte-zoomslider">
                  <div className="elementor-widget-container">
                    <div
                      className="lte-slider-zoom zoom-default zoom-origin-center-center lte-zs-overlay-black bullets-bottom"
                      data-zs-overlay="black"
                      data-zs-initzoom="1.2"
                      data-zs-speed="20000"
                      data-zs-interval="4500"
                      data-zs-switchSpeed="7000"
                      data-zs-arrows="false"
                      data-zs-bullets="bottom"
                      data-zs-src={zsAttr}
                    >
                      <div className="container lte-zs-slider-wrapper">
                        <div className="lte-zs-slider-inner visible">
                          <div className="lte-heading lte-size-lg lte-style-subheader-italic lte-uppercase lte-color-white">
                            <div className="lte-heading-content">
                              <h2 className="lte-header">{clubName} <span> {new Date().getFullYear()}/{(new Date().getFullYear()+1).toString().slice(2)} </span></h2>
                            </div>
                          </div>
                          <div className="lte-btn-wrap" style={{ marginTop: 12 }}>
                            <a href="/blog" className="lte-btn btn-lg color-hover-white"><span className="lte-btn-inner"><span className="lte-btn-before"></span>Zjistit více</span></a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Upcoming matches + table wrappers (content is rendered by TS above matching facr-frontend behavior) */}
        <div className="elementor-element lte-background-black e-flex e-con-boxed e-con e-parent" data-core-v316-plus="true">
          <div className="e-con-inner">
            <div className="elementor-widget elementor-widget-football-upcoming">
              <div className="elementor-widget-container">
                <div id="facr-upcoming" className="lte-football-upcoming"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Blog latest */}
        <div className="elementor-element e-flex e-con-boxed e-con e-parent" data-core-v316-plus="true">
          <div className="e-con-inner">
            <div className="elementor-element lte-heading-style-header-subheader elementor-widget elementor-widget-lte-header">
              <div className="elementor-widget-container">
                <div className="lte-heading lte-style-header-subheader lte-uppercase lte-subcolor-main has-subheader heading-tag-h3 heading-subtag-h6">
                  <div className="lte-heading-content">
                    <h6 className="lte-subheader">Náš Blog</h6>
                    <h3 className="lte-header">Aktuální zprávy z klubu</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="elementor-element elementor-widget elementor-widget-lte-blog">
              <div className="elementor-widget-container">
                <div className="blog lte-blog-sc row centered layout-posts">
                  <div id="latest-blog-items" className="row masonry" aria-live="polite"></div>
                </div>
              </div>
            </div>
            <div className="elementor-element elementor-widget elementor-widget-lte-button" style={{ marginTop: 12 }}>
              <div className="elementor-widget-container">
                <div className="lte-btn-wrap">
                  <a href="/blog" className="lte-btn btn-xs btn-transparent color-hover-default"><span className="lte-btn-inner"><span className="lte-btn-before"></span>Více novinek</span></a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="elementor-element e-flex e-con-boxed e-con e-parent" data-core-v316-plus="true">
          <div className="e-con-inner">
            <div className="elementor-element lte-heading-style-header-subheader elementor-widget elementor-widget-lte-header">
              <div className="elementor-widget-container">
                <div className="lte-heading lte-style-header-subheader lte-uppercase lte-subcolor-main has-subheader heading-tag-h3 heading-subtag-h6">
                  <div className="lte-heading-content">
                    <p className="Badge u-mb-8" id="facr-comp-badge" style={{ marginBottom: 0 }}>Soutěž</p><br />
                    <h6 className="lte-subheader"><span> Sezóna </span> {new Date().getFullYear()}-{(new Date().getFullYear()+1).toString().slice(2)}</h6>
                    <h3 className="lte-header">Tabulka bodů</h3>
                  </div>
                </div>
              </div>
            </div>
            <div id="facr-comp-tabs" className="u-mb-8" style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}></div>
            <div className="elementor-element elementor-widget elementor-widget-football-table">
              <div className="elementor-widget-container">
                <table className="lte-football-table">
                  <thead>
                    <tr>
                      <th>#</th><th></th><th className="lte-name">Klub</th><th>Z</th><th>V</th><th>R</th><th>P</th><th>Skóre</th><th>B</th>
                    </tr>
                  </thead>
                  <tbody id="facr-table-body"></tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Videos */}
        <div className="elementor-element lte-background-gray e-flex e-con-boxed e-con e-parent" data-core-v316-plus="true">
          <div className="e-con-inner">
            <div className="elementor-element lte-heading-align-center elementor-widget elementor-widget-lte-header">
              <div className="elementor-widget-container">
                <div className="lte-heading lte-style-default lte-uppercase lte-subcolor-white has-watermark heading-tag-h3 heading-subtag-h6">
                  <div className="lte-heading-content">
                    <span className="lte-watermark">Sestřihy zápasů</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="elementor-element e-flex e-con-boxed e-con e-child">
              <div className="e-con-inner">
                <div className="elementor-element e-con-full e-flex e-con e-child">
                  <div className="elementor-element elementor-widget elementor-widget-lte-blog">
                    <div className="elementor-widget-container">
                      <div className="blog lte-blog-sc row centered layout-posts-large hideLastOdd lte-grid-bg">
                        <div id="latest-video-feature" className="items col-xl-12 col-lg-12 col-md-12 col-sm-12 col-ms-12 col-xs-12" aria-live="polite"></div>
                      </div>
                      <div className="clearfix"></div>
                    </div>
                  </div>
                </div>
                <div className="elementor-element e-con-full e-flex e-con e-child">
                  <div className="elementor-element elementor-widget elementor-widget-lte-blog">
                    <div className="elementor-widget-container">
                      <div className="blog lte-blog-sc row centered layout-posts lte-grid-bg">
                        <div id="latest-videos-grid" className="row" aria-live="polite"></div>
                      </div>
                      <div className="clearfix"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social embeds */}
        <div id="social" className="elementor-element e-flex e-con-boxed e-con e-parent" aria-labelledby="social-heading">
          <div className="e-con-inner">
            <div className="elementor-element lte-heading-align-center elementor-widget elementor-widget-lte-header">
              <div className="elementor-widget-container">
                <div className="lte-heading lte-style-default lte-uppercase heading-tag-h3">
                  <div className="lte-heading-content">
                    <h3 id="social-heading" className="lte-header">Sledujte nás</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="row centered" style={{ marginTop: 20 }}>
              <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
                <div id="fb-root"></div>
                <div className="fb-page"
                  data-href={settings?.facebook_url || 'https://www.facebook.com/'}
                  data-height="500"
                  data-small-header="false"
                  data-adapt-container-width="true"
                  data-hide-cover="false"
                  data-show-facepile="true"
                  data-tabs="timeline"
                  data-width="600">
                  <blockquote className="fb-xfbml-parse-ignore">
                    <a href={settings?.facebook_url || '#'}>{settings?.club_name || 'Klub'}</a>
                  </blockquote>
                </div>
              </div>
              <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-xs-12">
                {igUrl ? (
                  <blockquote className="instagram-media" data-instgrm-permalink={igUrl} data-instgrm-version="14" style={{ background: '#FFF', border: 0, borderRadius: 3, boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)', margin: 1, maxWidth: 658, minWidth: 326, padding: 0, width: '99.375%' }}></blockquote>
                ) : (
                  <a href={settings?.instagram_url || 'https://www.instagram.com/'} target="_blank" rel="noreferrer">Instagram</a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sponsors grid */}
        {sponsorsGrid}
        
        {/* Team section */}
        <div id="tym" className="elementor-element elementor-element-ed06b06 e-con-full e-flex e-con e-parent" data-core-v316-plus="true">
          <div className="elementor-element lte-heading-style-header-subheader lte-heading-align-center lte-watermark-offset-1 elementor-widget elementor-widget-lte-header">
            <div className="elementor-widget-container">
              <div className="lte-heading lte-style-header-subheader lte-uppercase lte-subcolor-main has-subheader has-watermark heading-tag-h3 heading-subtag-h6">
                <div className="lte-heading-content">
                  <h6 className="lte-subheader">náš tým</h6>
                  <h3 className="lte-header">prohlédněte si náš tým</h3>
                  <span className="lte-watermark">náš tým</span>
                </div>
              </div>
            </div>
          </div>
          <div id="gender-switcher" aria-label="Přepínač pohlaví týmu" style={{ display:'flex', gap:8, justifyContent:'center', margin:'10px 0' }}>
            <button type="button" className="switch-btn active" data-gender="men">Muži</button>
            <button type="button" className="switch-btn" data-gender="women">Ženy</button>
          </div>
        </div>
        <div id="team-section-1" className="elementor-element e-con-full lte-background-black e-flex e-con e-parent" data-core-v316-plus="true">
          <div className="elementor-widget elementor-widget-lte-team">
            <div className="elementor-widget-container">
              <div className="team-preloader" id="team-preloader-1" aria-hidden="true" style={{ display:'none', alignItems:'center', justifyContent:'center', gap:6, color:'#fff', padding:'8px 0' }}>
                <div className="spinner" style={{ width:16, height:16, border:'2px solid rgba(255,255,255,.3)', borderTopColor:'#fff', borderRadius:'50%' }} />
                <div className="loading-text">Načítání…</div>
              </div>
              <div className="lte-swiper-slider-wrapper">
                <div className="lte-swiper-slider swiper-container lte-team-list lte-team-layout-default" data-space-between="30" data-arrows="sides-outside" data-autoplay="0" data-loop="" data-speed="1000" data-effect="coverflow" data-slides-per-group="-1" data-touch-move="0.2" data-breakpoints="5;4;4;3;3;1">
                  <div className="swiper-wrapper" id="team-swiper-wrapper-1"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Merch section */}
        <div id="merch" className="elementor-element elementor-element-merch e-flex e-con-boxed e-con e-parent" data-core-v316-plus="true">
          <div className="e-con-inner">
            <div className="elementor-element lte-heading-style-header-subheader elementor-widget elementor-widget-lte-header">
              <div className="elementor-widget-container">
                <div className="lte-heading lte-style-header-subheader lte-uppercase lte-subcolor-main has-subheader heading-tag-h3 heading-subtag-h6">
                  <div className="lte-heading-content">
                    <h6 className="lte-subheader">Fanshop</h6>
                    <h3 className="lte-header">Klubové oblečení</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="elementor-element elementor-widget elementor-widget-lte-products">
              <div className="elementor-widget-container">
                <div className="lte-swiper-slider-wrapper">
                  <div className="lte-swiper-slider swiper-container lte-products lte-team-list"
                    data-space-between="30" data-arrows="sides-outside" data-autoplay="0" data-loop="" data-speed="1000"
                    data-effect="coverflow" data-slides-per-group="-1" data-touch-move="0.2" data-breakpoints="4;3;3;2;2;1">
                    <div className="swiper-wrapper">
                      {(merch || []).slice(0, 12).map((it) => (
                        <div key={it.id} className="lte-item swiper-slide">
                          <div className="lte-team-item">
                            <a className="lte-image" href={it.url || '#'} target="_blank" rel="noreferrer">
                              {/* eslint-disable-next-line jsx-a11y/alt-text */}
                              <img loading="lazy" decoding="async" width={800} height={800} src={assetUrl(it.image_url) || '/dist/img/logo-club-empty.svg'} className="attachment-full size-full" />
                            </a>
                            <div className="lte-descr">
                              <a href={it.url || '#'} target="_blank" rel="noreferrer"><h4 className="lte-header">{it.title}</h4></a>
                              <p className="lte-subheader"><span>{typeof it.price === 'number' ? `${it.price} ${it.currency || 'Kč'}` : ''}</span></p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PremiumLayout>
  );
};

const PremiumHomePage: React.FC = () => {
  const { data: settings } = usePublicSettings();
  const clubName = settings?.club_name || 'Fotbal Club';
  const clubId = settings?.club_id || FACR_CLUB_ID;
  const clubType = settings?.club_type || FACR_CLUB_TYPE;

  const [heroArticles, setHeroArticles] = React.useState<Article[]>([]);
  const [latestArticles, setLatestArticles] = React.useState<Article[]>([]);
  const [newsItems, setNewsItems] = React.useState<NewsListItem[]>([]);
  const [standingsRows, setStandingsRows] = React.useState<StandingRow[]>([]);
  const [nextMatch, setNextMatch] = React.useState<NextMatchData | null>(null);
  const [nextCompetitionName, setNextCompetitionName] = React.useState<string | undefined>(undefined);
  const [nextMatchCountdown, setNextMatchCountdown] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    let cancelled = false;

    const parseCZDateTime = (s: string): Date | null => {
      try {
        const [d, t] = String(s || '').split(' ');
        const parts = (d || '').split('.').map((n) => Number(n));
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        const [hh, mm] = (t || '').split(':').map((n) => Number(n));
        if (!day || !month || !year) return null;
        return new Date(year, (month || 1) - 1, day, hh || 0, mm || 0);
      } catch {
        return null;
      }
    };

    const load = async () => {
      // Articles (featured + latest)
      try {
        const [featuredResp, latestResp] = await Promise.all([
          getFeaturedArticles({ page_size: 4 }).catch(() => ({ data: [] as Article[] })),
          getArticles({ page: 1, page_size: 8, published: true }).catch(() => ({ data: [] as Article[] })),
        ]);
        if (cancelled) return;
        const featured = (featuredResp?.data || []) as Article[];
        const latest = (latestResp?.data || []) as Article[];
        setHeroArticles(featured.slice(0, 3));
        setLatestArticles(latest);

        const featuredIds = new Set(featured.map((a) => a.id));
        const combinedForList = [...featured, ...latest].filter((a) => !featuredIds.has(a.id));
        const mappedNews: NewsListItem[] = combinedForList.slice(0, 8).map((a) => ({
          id: a.id,
          title: a.title,
          excerpt: (a as any).excerpt || undefined,
          image: a.image_url,
          slug: a.slug,
        }));
        setNewsItems(mappedNews);
      } catch {}

      // FACR data for next match + standings
      try {
        if (!clubId) return;
        const [clubInfo, tableInfo] = await Promise.all([
          facrApi.getClub(String(clubId), (clubType as any) || 'football').catch(() => null),
          facrApi.getClubTable(String(clubId), (clubType as any) || 'football').catch(() => null),
        ]);
        if (cancelled) return;

        // Next match selection (nearest future; fallback to last played)
        if (clubInfo && Array.isArray((clubInfo as any).competitions)) {
          const comps: any[] = (clubInfo as any).competitions || [];
          const now = Date.now();
          let best: { m: any; compName: string; dt: Date } | null = null;
          comps.forEach((c: any) => {
            const compName = c.name || c.code || 'Soutěž';
            (c.matches || []).forEach((m: any) => {
              const dt = parseCZDateTime(m.date_time);
              if (!dt) return;
              const ts = dt.getTime();
              if (ts < now) return;
              if (!best || ts < best.dt.getTime()) {
                best = { m, compName, dt };
              }
            });
          });

          if (!best && comps.length > 0) {
            const c = comps[0];
            const list = (c.matches || []).slice().reverse();
            const m = list[0];
            if (m) {
              const dt = parseCZDateTime(m.date_time) || new Date();
              best = { m, compName: c.name || c.code || 'Soutěž', dt };
            }
          }

          if (best && !cancelled) {
            setNextMatch({
              competition: best.compName,
              home: best.m.home,
              home_id: best.m.home_id,
              home_logo_url: best.m.home_logo_url,
              away: best.m.away,
              away_id: best.m.away_id,
              away_logo_url: best.m.away_logo_url,
            });
            setNextCompetitionName(best.compName);
            try {
              const label = `${best.dt.toLocaleDateString('cs-CZ')} ${best.dt.toLocaleTimeString('cs-CZ', {
                hour: '2-digit',
                minute: '2-digit',
              })}`;
              setNextMatchCountdown(label);
            } catch {
              setNextMatchCountdown(undefined);
            }
          }
        }

        // Standings – pick first competition with rows
        if (tableInfo && Array.isArray((tableInfo as any).competitions)) {
          const compsTbl: any[] = (tableInfo as any).competitions || [];
          const first = compsTbl.find((c: any) => Array.isArray(c.table?.overall) && c.table.overall.length > 0);
          if (first && Array.isArray(first.table?.overall)) {
            setStandingsRows(first.table.overall as StandingRow[]);
          }
        }
      } catch {}
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [clubId, clubType]);

  const heroPrimary = heroArticles[0];

  const spartaSliderArticles = React.useMemo(() => {
    const byId = new Map<number, Article>();
    [...heroArticles, ...latestArticles].forEach((a) => {
      if (!a) return;
      if (!byId.has(a.id)) byId.set(a.id, a);
    });
    return Array.from(byId.values())
      .slice(0, 10)
      .map((a) => ({
        id: String(a.id),
        title: a.title,
        slug: a.slug || String(a.id),
        image: assetUrl(a.image_url) || '/images/news/placeholder.jpg',
        categories: ((a as any).category?.name ? [(a as any).category.name] : []) as string[],
        date: (a as any).published_at || (a as any).created_at || new Date().toISOString(),
      }));
  }, [heroArticles, latestArticles]);

  const stripHtml = (html?: string) => {
    if (!html) return '';
    try {
      return html.replace(/<[^>]+>/g, ' ');
    } catch {
      return html;
    }
  };

  return (
    <PremiumLayout>
      {/* Sparta hero */}
      <Box className="sparta-hero-container sparta-container sparta-section">
        {heroPrimary && (
          <>
            <Box className="sparta-hero-background">
              <img
                src={assetUrl(heroPrimary.image_url) || '/dist/img/logo-club-empty.svg'}
                alt={heroPrimary.title}
              />
            </Box>
            <Box className="sparta-hero-article">
              <Box className="sparta-hero-categories">
                <span className="sparta-hero-category-badge">{clubName}</span>
                {(heroPrimary as any).category?.name && (
                  <>
                    <Box className="sparta-hero-separator" />
                    <span>{(heroPrimary as any).category.name}</span>
                  </>
                )}
              </Box>
              <Heading as="h1" className="sparta-hero-title">
                {heroPrimary.title}
              </Heading>
              {heroPrimary.content && (
                <Text className="sparta-hero-description">
                  {(() => {
                    const plain = stripHtml(heroPrimary.content);
                    return plain.length > 180 ? `${plain.slice(0, 180)}…` : plain;
                  })()}
                </Text>
              )}
              <Box mt={4} className="sparta-hero-button">
                <a
                  href={heroPrimary.slug ? `/news/${heroPrimary.slug}` : `/articles/${heroPrimary.id}`}
                  className="sparta-button-primary"
                >
                  Číst článek
                </a>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* News slider */}
      {spartaSliderArticles.length > 0 && (
        <Box className="sparta-container sparta-section">
          <SpartaHorizontalSlider
            title="Aktuality"
            titleLink="/blog"
            articles={spartaSliderArticles}
          />
        </Box>
      )}

      {/* Next match + standings */}
      {(nextMatch || standingsRows.length > 0) && (
        <Box className="sparta-container sparta-section">
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
            {nextMatch && (
              <Box gridColumn={{ base: 'span 1', lg: 'span 2' }}>
                <Heading as="h2" size="md" mb={3} textTransform="uppercase">
                  Nejbližší zápas
                </Heading>
                <NextMatch
                  data={nextMatch}
                  competitionName={nextCompetitionName}
                  countdown={nextMatchCountdown}
                />
              </Box>
            )}
            {standingsRows.length > 0 && (
              <Box>
                <Heading as="h2" size="md" mb={3} textTransform="uppercase">
                  Tabulka
                </Heading>
                <StandingsCard rows={standingsRows} />
              </Box>
            )}
          </SimpleGrid>
        </Box>
      )}

      {/* News list */}
      <Box className="sparta-container sparta-section">
        <Heading as="h2" size="md" mb={3} textTransform="uppercase">
          Novinky
        </Heading>
        <NewsList
          items={newsItems}
          seeAllHref="/blog"
          seeAllLabel="Všechny články"
        />
      </Box>

      {/* Videos */}
      <Box className="sparta-container sparta-section">
        <VideosSection variant="grid" />
      </Box>

      {/* Merch */}
      <Box className="sparta-container sparta-section">
        <MerchSection variant="carousel" />
      </Box>
    </PremiumLayout>
  );
};

export default PremiumHomePage;
